import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { toArabicNumerals } from '../utils/arabicNumbers';

const FileUpload = () => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setUploadStatus('');
    setUploadResult(null);
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError(t('upload.selectFile'));
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    setIsUploading(true);
    setUploadStatus(t('upload.uploading'));
    setError('');

    try {
      const response = await fetch(`${apiUrl}/upload-applications`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      
      if (response.ok) {
        const processedCount = result.applications_count || 
                              (result.message && result.message.match(/\d+/)) || 
                              'عدة';
        const arabicCount = toArabicNumerals(processedCount.toString());
        setUploadStatus(`${t('upload.success')}: تم معالجة ${arabicCount} طلب ترقية بنجاح`);
        setUploadResult(result);
      } else {
        setError(`${t('upload.uploadFailed')}: ${result.detail || 'خطأ غير معروف'}`);
        setUploadStatus('');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(t('upload.error'));
      setUploadStatus('');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${apiUrl}/application-template`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the Excel file as blob
      const blob = await response.blob();
      
      // Create and download the Excel file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'promotion_applications_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Template download error:', error);
      setError(t('upload.templateFailed'));
    }
  };

  const formatFileSize = (bytes) => {
    const sizeInKB = (bytes / 1024).toFixed(2);
    return toArabicNumerals(sizeInKB) + ' كيلوبايت';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <UploadIcon sx={{ fontSize: 40, color: 'white' }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('upload.title')}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, color: 'white', opacity: 1 }}>
              {t('upload.description')}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Upload Status */}
      {uploadStatus && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {uploadStatus}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Template Download Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DownloadIcon color="primary" />
                <Typography variant="h6">
                  {t('upload.downloadTemplate')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('upload.templateDescription')}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                fullWidth
                color="primary"
              >
                {t('upload.downloadExcelTemplate')}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* File Upload Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FileIcon color="primary" />
                <Typography variant="h6">
                  {t('upload.selectFileTitle')}
                </Typography>
              </Box>
              
              <input
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                id="file-upload-button"
                type="file"
                onChange={handleFileSelect}
                disabled={isUploading}
              />
              <label htmlFor="file-upload-button">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={isUploading}
                >
                  {selectedFile ? selectedFile.name : t('upload.chooseFile')}
                </Button>
              </label>

              {selectedFile && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('upload.fileInfo')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      icon={<FileIcon />}
                      label={`${t('upload.size')}: ${formatFileSize(selectedFile.size)}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={selectedFile.type || t('upload.unknownType')}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              )}

              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                startIcon={<UploadIcon />}
                fullWidth
                color="primary"
              >
                {isUploading ? t('upload.uploading') : t('upload.uploadFile')}
              </Button>

              {isUploading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    {t('upload.processingFile')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Upload Results */}
        {uploadResult && uploadResult.status === 'success' && (
          <Grid size={12}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SuccessIcon color="success" />
                  <Typography variant="h6">
                    {t('upload.uploadResults')}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Chip
                    icon={<SuccessIcon />}
                    label={`${t('upload.applicationsProcessed')}: ${toArabicNumerals(uploadResult.applications_count.toString())}`}
                    color="success"
                    variant="outlined"
                  />
                </Box>

                {uploadResult.preview && uploadResult.preview.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      {t('upload.preview')}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('upload.employeeName')}</TableCell>
                            <TableCell>{t('upload.currentPosition')}</TableCell>
                            <TableCell>{t('upload.newPosition')}</TableCell>
                            <TableCell>{t('upload.performance')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {uploadResult.preview.slice(0, 3).map((app, index) => (
                            <TableRow key={index}>
                              <TableCell>{app.employee_name}</TableCell>
                              <TableCell>{app.current_position_ar || app.current_position_en}</TableCell>
                              <TableCell>{app.new_position_ar || app.new_position_en}</TableCell>
                              <TableCell>{app.annual_performance}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Instructions */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InfoIcon color="primary" />
                <Typography variant="h6">
                  {t('upload.instructions')}
                </Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemText primary={t('upload.instruction1')} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t('upload.instruction2')} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t('upload.instruction3')} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t('upload.instruction4')} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={t('upload.instruction5')} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Supported Formats */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('upload.supportedFormats')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <FileIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={t('upload.excelFormat')} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <FileIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={t('upload.csvFormat')} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FileUpload;
